import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { theme } from 'antd';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import type { RoyaltyReportDetailOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import {
  ROYALTY_REPORT_DEFAULT_TAB,
  ROYALTY_REPORT_SHOW_TABS,
  ROYALTY_REPORT_STATEMENT_PARAM,
} from '../constants';
import { RoyaltyReportShowHeader } from './RoyaltyReportShowHeader';
import { RoyaltyReportGeneralTab } from './RoyaltyReportGeneralTab';
import { RoyaltyReportBreakdownTab } from './RoyaltyReportBreakdownTab';

export const RoyaltyReportShow = () => {
  const { token } = theme.useToken();
  const { query } = useShow<RoyaltyReportDetailOut>({
    resource: 'settlement.royalty_reports',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  useRecordDocumentTitle(
    'settlement.royalty_reports',
    record && dayjs(record.period).format('YYYY-MM')
  );

  const [activeTab, onTabChange] = useShowTab(
    ROYALTY_REPORT_SHOW_TABS,
    ROYALTY_REPORT_DEFAULT_TAB
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const statementId =
    Number(searchParams.get(ROYALTY_REPORT_STATEMENT_PARAM)) || undefined;

  const selectStatement = (id: number, openTab = false) =>
    // One update: two back-to-back setSearchParams calls would drop the first.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(ROYALTY_REPORT_STATEMENT_PARAM, String(id));
        if (openTab) next.set('tab', 'breakdown');
        return next;
      },
      { replace: true }
    );

  return (
    <Show isLoading={isLoading} contentProps={bareShowContentProps}>
      {record && (
        <>
          <RoyaltyReportShowHeader
            record={record}
            activeTab={activeTab}
            onTabChange={onTabChange}
            onChanged={() => query.refetch()}
          />
          <div style={{ marginTop: token.margin }}>
            {activeTab === 'general' && (
              <RoyaltyReportGeneralTab
                record={record}
                onOpenBreakdown={(id) => selectStatement(id, true)}
              />
            )}
            {activeTab === 'breakdown' && (
              <RoyaltyReportBreakdownTab
                record={record}
                statementId={statementId}
                onStatementChange={selectStatement}
              />
            )}
          </div>
        </>
      )}
    </Show>
  );
};
